<template>
  <div
    v-if="filterValues"
    class="base-filter"
    @click.capture="$emit('interaction')"
  >
    <div class="base-filter--header">
      <BaseInput
        v-if="!disableSearch"
        v-model="filterValues.search"
        type="text"
        placeholder="Suche..."
        icon-left="fa-solid fa-search"
        outlined
        rounded
        x-small
        aria-label="Suchfeld zum Filtern der Einträge"
      />
      <div
        v-if="hasFilters"
        class="base-filter--handler accessibility-outline"
        role="button"
        tabindex="0"
        :aria-expanded="expanded"
        :aria-label="`Filter ${expanded ? 'aufklappen' : 'zuknappen'}`"
        @click="expanded = !expanded"
        @keydown.enter.prevent="expanded = !expanded"
        @keydown.space.prevent="expanded = !expanded"
      >
        <BaseIcon
          :class="{ 'base-filter--expanded': expanded }"
          icon="fa-light fa-chevron-down"
        />
        <span>Filter</span>
        <span class="base-filter--divider" />
      </div>
    </div>
    <Expansion
      v-model="expanded"
      class="base-filter--content"
      title="Filter"
      hide-header
    >
      <div class="filters">
        <div
          v-for="item in visibleFilters"
          :key="item.name"
          class="item"
          :class="item.range ? 'wide' : ''"
        >
          <component
            :is="item.component"
            :value="getFilterValue(item)"
            v-bind="item"
            outlined
            rounded
            @update="updateFilterValue(item, $event)"
          />
        </div>
      </div>
      <div class="additional-toggle">
        <BaseButtonV2
          v-if="additionalFilters.length"
          type="outlined"
          medium
          @click="toggleAdditionals(!showAdditionals)"
        >
          <BaseIcon icon="fal fa-filter-list" /> {{ showAdditionals ? 'Weniger' : 'Mehr' }} Filter
        </BaseButtonV2>
      </div>
    </Expansion>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'BaseFilter',
  model: {
    prop: 'value',
    event: 'update',
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Array,
      default: () => [],
    },
    delay: {
      type: Number,
      default: 1000,
    },
    tableSlug: {
      type: String,
      default: '',
    },
    cache: {
      type: Object,
      default: () => ({}),
    },
    baseTableMounted: {
      type: Boolean,
      default: false,
    },
    disableSearch: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      defaults: [],
      additionals: [],
      showAdditionals: false,
      debouncedUpdate: null,
      expanded: false,
      filterValues: this.value || {},
      timer: null,
    };
  },

  computed: {
    hasFilters() {
      return !!this.filters.filter((item) => item.name !== 'search').length;
    },
    defaultFilters() {
      return this.defaults;
    },
    additionalFilters() {
      return this.additionals;
    },
    visibleFilters() {
      if (this.showAdditionals) {
        return [...this.defaultFilters, ...this.additionalFilters];
      }
      return this.defaultFilters;
    },
  },

  watch: {
    'filters.length': {
      deep: true,
      handler(val, prev) {
        if (val !== prev) {
          this.setFilters();
        }
      },
    },
    filterValues: {
      deep: true,
      handler(val) {
        this.debouncedUpdate(val);
      },
    },
  },

  mounted() {
    this.setFilters();
    this.debouncedUpdate = _.debounce(this.update, this.delay);
  },

  methods: {
    toggleAdditionals(value) {
      this.showAdditionals = value;

      this.filterValues = Object.entries(this.filterValues).reduce((acc, [key, val]) => {
        const currentFilter = this.visibleFilters.map((item) => item.name);

        if (!this.disableSearch && key === 'search') {
          acc[key] = val;

          return acc;
        }
        if (currentFilter.includes(key)) {
          acc[key] = val;
        }

        return acc;
      }, {});
    },
    update(val) {
      this.$emit('update', { ...val });
    },
    getFilters(filter) {
      return filter.map((item) => {
        switch (item.type.toLowerCase().trim()) {
          case 'select':
          case 'baseselect':
            return {
              ...this.createFilter(item),
              component: 'BaseSelect',
            };
          case 'date':
          case 'basedatetimepicker':
            return {
              ...this.createFilter(item),
              component: 'BaseDateTimePicker',
            };
          case 'rangepicker':
            return {
              ...this.createFilter(item),
              component: 'BaseDateTimePicker',
              range: true,
              autoPrefill: true,
            };
          case 'text':
          case 'baseinput':
            return {
              ...this.createFilter(item),
              component: 'BaseInput',
            };
          case 'switch':
            return {
              ...this.createFilter(item),
              component: 'BaseSwitch',
            };
          case 'checkbox':
            return {
              ...this.createFilter(item),
              component: 'BaseCheckbox',
            };
          default:
            return {
              ...this.createFilter(item),
              component: 'BaseInput',
            };
        }
      });
    },
    createFilter(item) {
      return {
        label: item.label,
        name: item.name,
        items: item.items,
        hide: item.hidden,
        value: item.value,
      };
    },
    setFilters() {
      this.setValues();

      this.defaults = this.getFilters(this.filters.filter((item) => !item.hidden && item.name !== 'search')); // TODO: remove search from backend response
      this.additionals = this.getFilters(this.filters.filter((item) => item.hidden && item.name !== 'search')); // TODO: remove search from backend response
    },
    hasValues(value) {
      const arrayCheck = !(Array.isArray(value) && value.length === 0);

      return value !== null && value !== undefined && value !== '' && arrayCheck;
    },
    setValues() {
      const hasValues = this.filters.filter((filter) => this.hasValues(filter.value));

      this.filterValues = hasValues.reduce((acc, item) => {
        if (this.filters.find((filter) => filter.name === item.name)?.hidden) {
          this.filters.find((filter) => filter.name === item.name).hidden = false;
        }
        if (!this.disableSearch && acc.search === undefined) acc.search = '';
        if (item.name === 'search') {
          if (!this.disableSearch) {
            acc[item.name] = this.filterValues?.[item.name] || item.value || null;
          }

          return acc;
        }
        acc[item.name] = this.filterValues?.[item.name] || item.value || null;

        return acc;
      }, {});
    },
    getFilterValue(item) {
      switch (item.component) {
        case 'BaseSwitch':
        case 'BaseCheckbox':
          return !!this.filterValues[item.name];
        default:
          return this.filterValues[item.name];
      }
    },
    reactiveUpdate(name, value) {
      this.filterValues = {
        ...this.filterValues,
        [name]: value,
      };
    },
    updateFilterValue(item, value) {
      switch (item.component) {
        case 'BaseSwitch':
        case 'BaseCheckbox':
          this.reactiveUpdate(item.name, value ? 1 : undefined);
          break;
        default:
          this.reactiveUpdate(item.name, value);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../sass/variables';

.base-filter {
  display: flex;
  flex-direction: column;

  &--header {
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
    gap: 1.5rem;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      grid-template-columns: auto 1fr;
    }
  }

  .base-input {
    width: auto;
  }

  &--handler {
    display: grid;
    grid-template-columns: auto auto 1fr;
    gap: 0.5rem;
    align-items: center;
    cursor: pointer;
  }

  .base-icon {
    transition: transform 575ms ease;
  }

  &--expanded {
    transform: rotate(-90deg);
  }

  &--divider {
    border-bottom: 1px solid #f2f2f2;
  }

  &--content {
    padding: 1rem 0;

    :deep(.expansion__content) {
      display: grid;
      gap: 20px;
      grid-template-columns: 1fr;

      @media (max-width: #{map_get($grid-breakpoints, 'lg') - 1px}) {
        .additional-toggle {
          margin-bottom: 20px;

          button {
            box-sizing: border-box;
            width: 100%;
          }
        }
      }

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        grid-template-columns: 1fr auto;

        .filters {
          margin-bottom: 20px;
        }
      }
    }
  }
}

.cross {
  position: absolute;
  top: -60px;
  right: 0px;
  bottom: 0;
  margin: auto;
  cursor: pointer;
  width: 15px;
  height: 15px;
  display: block;
  color: red;
}

.filters {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(1, 1fr);

  @media (min-width: map_get($grid-breakpoints, 'md')) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: map_get($grid-breakpoints, 'xl')) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: map_get($grid-breakpoints, 'xxl')) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (min-width: 1600px) {
    grid-template-columns: repeat(5, 1fr);
  }

  @media (min-width: 2048px) {
    grid-template-columns: repeat(6, 1fr);
  }

  .item {
    position: relative;
    height: fit-content;

    &.wide {
      @media (min-width: map_get($grid-breakpoints, 'md')) {
        grid-column: auto / span 2;
      }
    }

    > div {
      width: 100%;
    }
  }

  & :deep(.picker) {
    height: 46px;
  }
}

.additional-toggle {
  padding: 5px 0;
}
</style>
